import { Icon } from '@iconify/react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { InputPrefix, InputSuffix, TextBox } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import useAuth from 'src/hooks/useAuth';
import useCompanyExportTreeApi from 'src/services/work_area/useCompanyExportTreeMutation';
import { downloadFileFromUrl } from 'src/utils/fileTransformation';
import { useContextSelector } from 'use-context-selector';
import { getFilterByFromOptionValue } from './useWorkAreaFilterOptions';
import { WorkAreaContext } from './WorkAreaContext';
import {
  WORK_AREA_HEADER_SEARCH_FIELD_ID,
  WORK_AREA_SEARCH_AUTOCOMPLETE,
  WORK_AREA_SEARCH_FIELD_NAME,
} from './workAreaSearchField';
import { useWorkAreaSearchResultsModal } from './WorkAreaSearchResultsModal';

const WorkAreaHeader = () => {
  const { t } = useTranslation();
  const { user, isLoggedInAsSuperAdmin } = useAuth();
  // Match CompanyTree / AuthContext: original Super Admin login keeps work-area behavior after impersonation.
  const isWorkAreaSuperAdminSession = Boolean(isLoggedInAsSuperAdmin);
  const selectedCompanyId = useContextSelector(
    WorkAreaContext,
    (context) => context.selectedCompanyId
  );
  const selectedCompanyName = useContextSelector(
    WorkAreaContext,
    (context) => context.selectedCompanyName
  );
  const companyTreeFilter = useContextSelector(
    WorkAreaContext,
    (context) => context.companyTreeFilter
  );
  const userCompaniesFilter = useContextSelector(
    WorkAreaContext,
    (context) => context.userCompaniesFilter
  );
  const [searchValue, setSearchValue] = useState('');
  const searchTerm = useMemo(() => searchValue.trim(), [searchValue]);
  const openSearchResults = useWorkAreaSearchResultsModal(searchTerm);

  const effectiveRootCompanyId = useMemo((): number | null => {
    if (selectedCompanyId != null) return selectedCompanyId;
    if (isWorkAreaSuperAdminSession) return null;
    if (user?.CompanyId != null) {
      const parsed = Number(user.CompanyId);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }, [selectedCompanyId, isWorkAreaSuperAdminSession, user?.CompanyId]);

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
  }, []);

  const handleOpenSearchResults = useCallback(() => {
    if (!searchTerm) {
      return;
    }
    openSearchResults({
      effectiveRootCompanyId,
      isSuperAdmin: isWorkAreaSuperAdminSession,
      selectedCompanyId,
      selectedCompanyName,
    });
    setSearchValue('');
  }, [
    openSearchResults,
    searchTerm,
    effectiveRootCompanyId,
    isWorkAreaSuperAdminSession,
    selectedCompanyId,
    selectedCompanyName,
  ]);

  const { exportExecute, exportLoading } = useCompanyExportTreeApi();
  const handleExportToExcel = useCallback(async () => {
    if (selectedCompanyId == null) return;
    const filter = getFilterByFromOptionValue(companyTreeFilter?.value);
    const clientFilterCandidate = getFilterByFromOptionValue(userCompaniesFilter?.value);
    try {
      const res = await exportExecute({
        companyId: selectedCompanyId,
        clientFilter: clientFilterCandidate,
        companyFilter: filter,
      });
      if (!res?.data?.downloadUrl) {
        return;
      }
      await downloadFileFromUrl(res.data.downloadUrl, `${selectedCompanyName}_Users.xls`);
      toast.success('File Exported successfully.');
    } catch {
      toast.error('An error occurred while exporting. Please try again.');
    }
  }, [
    selectedCompanyId,
    companyTreeFilter?.value,
    userCompaniesFilter?.value,
    exportExecute,
    selectedCompanyName,
  ]);

  const searchPlaceholder = useMemo(() => {
    if (!isWorkAreaSuperAdminSession) {
      return t('searchByContactName');
    }
    if (selectedCompanyId != null) {
      return t('searchByContactNameInSelectedCompany');
    }
    return t('searchByCompanyOrContactName');
  }, [t, selectedCompanyId, isWorkAreaSuperAdminSession]);

  // Search root: selected tree company, else Super Admin → all companies (null), else JWT CompanyId.
  return (
    <div className="flex items-center justify-between w-full px-4 py-2">
      <div className="flex items-center gap-2 flex-[1_1_250px] min-w-[200px] max-w-[700px]">
        <FieldWrapper className="flex flex-row items-center space-x-2 w-full">
          <Label
            editorId={WORK_AREA_HEADER_SEARCH_FIELD_ID}
            className="text-sm flex items-center whitespace-nowrap font-semibold"
          >
            {t('searchBy')}
          </Label>
          <form
            className="flex flex-1 min-w-0 items-center gap-2"
            autoComplete={WORK_AREA_SEARCH_AUTOCOMPLETE}
            onSubmit={(event) => {
              event.preventDefault();
              handleOpenSearchResults();
            }}
          >
            <div className="flex-1 min-w-0">
              <TextBox
                id={WORK_AREA_HEADER_SEARCH_FIELD_ID}
                name={WORK_AREA_SEARCH_FIELD_NAME}
                max={500}
                maxLength={500}
                size="medium"
                style={{
                  fontSize: '14px',
                }}
                placeholder={searchPlaceholder}
                width="100%"
                value={searchValue}
                autoComplete={WORK_AREA_SEARCH_AUTOCOMPLETE}
                onChange={(event) => {
                  setSearchValue(String(event.value ?? ''));
                }}
                prefix={() => (
                  <InputPrefix className="pl-2">
                    <Icon height={'18'} width={'18'} color="#AAAAAA" icon={'lucide:search'} />
                  </InputPrefix>
                )}
                suffix={() => (
                  <InputSuffix className="pr-2">
                    {searchValue && (
                      <button
                        type="button"
                        onClick={handleClearSearch}
                        aria-label={t('clearSearch')}
                        className="flex items-center justify-center"
                      >
                        <Icon height={'16'} width={'16'} color="#AAAAAA" icon={'lucide:x'} />
                      </button>
                    )}
                  </InputSuffix>
                )}
              />
            </div>
            <Button
              type="submit"
              color="info"
              size="md"
              disabled={!searchTerm}
              className="flex-shrink-0"
            >
              {t('Search')}
            </Button>
          </form>
        </FieldWrapper>
      </div>
      <div className="flex-shrink-0">
        <Button
          color="info"
          size="md"
          disabled={selectedCompanyId == null || exportLoading}
          loading={exportLoading}
          onClick={handleExportToExcel}
        >
          {t('exportToExcel')}
        </Button>
      </div>
    </div>
  );
};

export default WorkAreaHeader;
